import { registerQuery } from 'data-fetching-client';
import http from 'hub-http/clients/apiClient';
import noAuthHttp from 'hub-http/clients/noAuthApiClient';
import { createRpcClientV2 } from 'rpc-client-utils';
import { findAllPurchasableProductsRpc as publicFindAllPurchasableProductsRpc, findPackagedQuantityPurchaseOptionsRpc as publicFindPackagedQuantityPurchaseOptionsRpc, findPurchaseOptionsRpc as publicFindPurchaseOptionsRpc, findQuantityPackPricingTiersRpc as publicFindQuantityPackPricingTiersRpc } from '../../__generated__/chirp/com/hubspot/purchase/options/rpc/PurchaseOptionsService';
import { findAllPurchasableProductsRpc as appFindAllPurchasableProductsRpc, findPackagedQuantityPurchaseOptionsRpc as appFindPackagedQuantityPurchaseOptionsRpc, findPurchaseOptionsRpc as appFindPurchaseOptionsRpc, findQuantityPackPricingTiersRpc as appFindQuantityPackPricingTiersRpc } from '../../__generated__/chirp/com/hubspot/purchase/options/rpc/PurchaseOptionsService-app';
import { transformAllPurchasableProductsResponse, transformFindPurchaseOptionsResponse, transformPagedQuantityPackResponse } from './dataTransformer';
const createRpcClient = (noAuth = false) => {
  return createRpcClientV2({
    hubHttpClient: noAuth ? noAuthHttp : http
  });
};
export const GET_ALL_PRICING_PURCHASABLE_PRODUCTS = registerQuery({
  fieldName: 'bulkPurchaseOptions',
  args: ['noAuth', 'currencyCode', 'language', 'preferredCountryCode'],
  async fetcher({
    noAuth,
    currencyCode,
    language,
    preferredCountryCode = 'US'
  }) {
    const rpcClient = createRpcClient(noAuth);
    const rpcToUse = noAuth ? publicFindAllPurchasableProductsRpc : appFindAllPurchasableProductsRpc;
    const userContext = noAuth ? {
      __typename: 'com.hubspot.purchase.options.models.rpc.context.AnonymousUserContext',
      currencyCode,
      language,
      preferredCountryCode
    } : {
      __typename: 'com.hubspot.purchase.options.models.rpc.context.TouchlessUserContext',
      currencyCode,
      language
    };
    const purchasableProductsResponse = await rpcClient.call(rpcToUse, {
      request: {
        userContext
      },
      timeout: 30000
    });
    return transformAllPurchasableProductsResponse(purchasableProductsResponse);
  }
});
export const GET_PURCHASE_OPTIONS = registerQuery({
  fieldName: 'purchaseOptions',
  args: ['noAuth', 'currencyCode', 'language', 'preferredCountryCode', 'products', 'term'],
  async fetcher({
    noAuth,
    currencyCode = 'USD',
    language,
    preferredCountryCode = 'US',
    products
  }) {
    const rpcClient = createRpcClient(noAuth);
    const rpcToUse = noAuth ? publicFindPurchaseOptionsRpc : appFindPurchaseOptionsRpc;
    const userContext = noAuth ? {
      __typename: 'com.hubspot.purchase.options.models.rpc.context.AnonymousUserContext',
      currencyCode,
      language,
      preferredCountryCode
    } : {
      __typename: 'com.hubspot.purchase.options.models.rpc.context.TouchlessUserContext',
      currencyCode,
      language
    };
    const purchaseOptionsResponse = await rpcClient.call(rpcToUse, {
      request: {
        products,
        userContext
      }
    });
    const transformedData = transformFindPurchaseOptionsResponse(purchaseOptionsResponse, products);
    return transformedData;
  }
});
export const GET_PAGED_QUANTITY_PACK_FIELD_NAME = 'pagedQuantityPack';
export const GET_PAGED_QUANTITY_PACK_ARGS = ['noAuth', 'currencyCode', 'language', 'preferredCountryCode', 'skuUuid', 'commitmentTerm', 'paymentTerm', 'quantityName', 'pageLength', 'additionalQuantity'];
export const GET_PAGED_QUANTITY_PACK = registerQuery({
  fieldName: GET_PAGED_QUANTITY_PACK_FIELD_NAME,
  args: GET_PAGED_QUANTITY_PACK_ARGS,
  async fetcher({
    noAuth,
    currencyCode = 'USD',
    language,
    preferredCountryCode = 'US',
    skuUuid,
    commitmentTerm,
    paymentTerm,
    quantityName,
    pageLength = 10,
    additionalQuantity = 0
  }) {
    const rpcClient = createRpcClient(noAuth);
    const rpcToUse = noAuth ? publicFindPackagedQuantityPurchaseOptionsRpc : appFindPackagedQuantityPurchaseOptionsRpc;
    const userContext = noAuth ? {
      __typename: 'com.hubspot.purchase.options.models.rpc.context.AnonymousUserContext',
      currencyCode,
      language,
      preferredCountryCode
    } : {
      __typename: 'com.hubspot.purchase.options.models.rpc.context.TouchlessUserContext',
      currencyCode,
      language
    };
    const response = await rpcClient.call(rpcToUse, {
      request: {
        skuUuid,
        quantityName,
        commitmentTerm,
        paymentTerm,
        pageLength,
        offset: {
          quantity: additionalQuantity
        },
        userContext
      }
    });
    return transformPagedQuantityPackResponse(response, commitmentTerm, paymentTerm);
  }
});
export const GET_QUANTITY_PACK_PRICING_TIERS_FIELD_NAME = 'quantityPackPricingTiers';
export const GET_QUANTITY_PACK_PRICING_TIERS_ARGS = ['noAuth', 'currencyCode', 'commitmentTerm', 'paymentTerm', 'quantityPackSkuUuid', 'language', 'preferredCountryCode'];
export const GET_QUANTITY_PACK_PRICING_TIERS = registerQuery({
  fieldName: GET_QUANTITY_PACK_PRICING_TIERS_FIELD_NAME,
  args: GET_QUANTITY_PACK_PRICING_TIERS_ARGS,
  async fetcher({
    noAuth,
    currencyCode = 'USD',
    commitmentTerm,
    paymentTerm,
    quantityPackSkuUuid,
    language,
    preferredCountryCode = 'US'
  }) {
    const rpcClient = createRpcClient(noAuth);
    const rpcToUse = noAuth ? publicFindQuantityPackPricingTiersRpc : appFindQuantityPackPricingTiersRpc;
    const userContext = noAuth ? {
      __typename: 'com.hubspot.purchase.options.models.rpc.context.AnonymousUserContext',
      currencyCode,
      language,
      preferredCountryCode
    } : {
      __typename: 'com.hubspot.purchase.options.models.rpc.context.TouchlessUserContext',
      currencyCode,
      language
    };
    return await rpcClient.call(rpcToUse, {
      request: {
        commitmentTerm,
        currencyCode,
        paymentTerm,
        quantityPackSkuUuid,
        userContext
      }
    });
  }
});